import React, { useState, lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  ShoppingCart,
  Plus,
  Minus,
  BedDouble,
  Bath,
  Sofa,
  Utensils,
  Home,
  Package,
  Gamepad2,
  UtensilsCrossed,
} from "lucide-react";
import { LazySection } from "@/components/LazySection";
import { useLeadGate, LeadGate } from "@/components/LeadGate";
import { furnitureData } from "@/lib/furnitureData";

const Footer = lazy(() => import("@/components/Footer"));

const FurniturePage = () => {
  // Default values: 6 bedrooms, 3 bathrooms, 2 living rooms
  const [bedrooms, setBedrooms] = useState(6);
  const [bathrooms, setBathrooms] = useState(3);
  const [livingRooms, setLivingRooms] = useState(2);

  const { unlocked, unlock } = useLeadGate();

  // Track viewed items using localStorage so customers know what they've already looked at
  const [viewedItems, setViewedItems] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("jnn_viewed_furniture");
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const markViewed = (link: string) => {
    setViewedItems((prev) => {
      if (prev.has(link)) return prev;
      const next = new Set(prev);
      next.add(link);
      try {
        localStorage.setItem("jnn_viewed_furniture", JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const clearViewed = () => {
    setViewedItems(new Set<string>());
    try {
      localStorage.removeItem("jnn_viewed_furniture");
    } catch {
      /* ignore */
    }
  };

  const handleIncrement = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    value: number,
  ) => setter(value + 1);
  const handleDecrement = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    value: number,
  ) => {
    if (value > 0) setter(value - 1);
  };

  const renderItemCard = (item: any) => (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => markViewed(item.link)}
      className={`flex items-center justify-between p-4 rounded-lg border transition-all group ${viewedItems.has(item.link) ? "border-green-500/40 bg-green-50/40" : "border-border hover:border-primary/50 hover:bg-muted/20"}`}
    >
      <div className="flex-1 pr-3">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4 className="font-medium group-hover:text-primary transition-colors">
            {item.name}
          </h4>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/20 shadow-sm">
            Qty: {item.qty || 1}
          </span>
          {viewedItems.has(item.link) && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-300 shadow-sm flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clipRule="evenodd"
                />
              </svg>
              Viewed
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{item.desc}</p>
      </div>
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF9900]/10 text-[#FF9900] group-hover:scale-110 transition-transform shrink-0">
        <svg viewBox="0 0 100 100" className="w-6 h-6" fill="currentColor">
          <path d="M63.6,56.5c-4.4,2.9-10.7,4.8-17.5,4.8c-14.8,0-26.6-8.5-26.6-21.7c0-12.8,11.5-21.7,26.6-21.7 c6.8,0,12.8,1.9,17,4.6v11.1c-4.6-3.7-10.4-5.8-16.5-5.8c-9.1,0-15.6,5.3-15.6,12.2c0,7.1,6.5,12.2,16.1,12.2 c5.6,0,10.9-1.9,14.8-5V56.5z M85.4,30.3v34.4h-10.6V56.8c-3.1,4.4-8.7,7.5-15.3,7.5c-10.6,0-17.7-7.3-17.7-17.2 c0-11.2,8.6-17.9,21.3-17.9c4.8,0,8.7,1,11.5,2.7v-2.2c0-5.8-4.2-9.2-11.5-9.2c-4.8,0-10.2,1.5-14.2,4.6v-9.5 c4.8-2.9,11.4-4.6,17.7-4.6C78.4,10.8,85.4,17.7,85.4,30.3z M74.6,48.1v-7.3c-2.4-1.2-5.6-2.2-9.2-2.2c-6.5,0-10.6,3.4-10.6,8.5 c0,4.8,3.6,8,8.7,8C68.9,55.1,72.7,52.2,74.6,48.1z" />
          <path d="M9.1,77.5c15.8,11.6,40.5,14.5,60.2,6.5c3.6-1.5,1.9-6.3-1.7-5.1c-19.4,7.3-42.5,4.6-56.8-5.3 C8.2,71.7,5.8,75.1,9.1,77.5z" />
          <path d="M72.2,80.9c-0.7-2.4,4.4-3.4,8-3.6c5.3-0.2,11.2,1.2,12.6,3.6c1.5,2.4-2.7,6.8-7.5,8.2 c-4.6,1.5-7.8-0.5-8.5-2.2c1.2,0.5,3.6,1.2,6.1,0.5c2.4-0.7,3.6-2.4,2.9-3.4c-0.5-1.2-3.4-1.9-6.8-1.7 C75.4,77.5,72.9,83.3,72.2,80.9z" />
        </svg>
      </div>
    </a>
  );

  const renderItemList = (
    title: string,
    items: any[],
    count: number,
    icon: React.ReactNode,
    idPrefix: string,
  ) => {
    if (count === 0) return null;

    return Array.from({ length: count }).map((_, roomIndex) => (
      <Card
        key={`${idPrefix}-${roomIndex}`}
        className="mb-8 border-primary/10 shadow-sm"
      >
        <CardHeader className="bg-muted/30 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              {icon}
            </div>
            <CardTitle className="text-2xl font-heading">
              {title} {count > 1 ? roomIndex + 1 : ""}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item, idx) => (
              <div key={idx}>{renderItemCard(item)}</div>
            ))}
          </div>
        </CardContent>
      </Card>
    ));
  };

  const renderLivingRooms = () => {
    if (livingRooms === 0) return null;
    return Array.from({ length: livingRooms }).map((_, roomIndex) => {
      const isMain = roomIndex === 0;
      const items = isMain
        ? furnitureData.livingRoom
        : furnitureData.extraLivingRoom;
      const title = isMain
        ? livingRooms > 1
          ? "Main Living Room"
          : "Living Room"
        : `Extra Living Room (Living Room ${roomIndex + 1})`;

      return (
        <Card
          key={`living-${roomIndex}`}
          className="mb-8 border-primary/10 shadow-sm"
        >
          <CardHeader className="bg-muted/30 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Sofa className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl font-heading">{title}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, idx) => (
                <div key={idx}>{renderItemCard(item)}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Airbnb Furniture Checklist & Shoppable Links"
        description="Build your custom Airbnb furniture list. Select your property size and get a complete, shoppable Amazon checklist for every room."
        canonical="https://www.jnnhomes.com/furniture"
      />
      <Navbar />

      <main className="pt-24 pb-20">
        {/* Header Section */}
        <section className="px-4 py-12 md:py-20 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              The Ultimate Airbnb <br className="hidden md:block" />
              <span className="text-primary">Furniture Checklist</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Configure your property size below to generate a complete,
              room-by-room shoppable list. We've hand-picked the best Amazon
              items for durability, style, and guest satisfaction.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Configuration Panel */}
          <Card className="mb-12 border-primary/20 shadow-md">
            <CardHeader className="bg-primary/5 border-b border-primary/10">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Home className="w-5 h-5 text-primary" />
                Configure Your Property
              </CardTitle>
              <CardDescription>
                Adjust the number of rooms to generate your custom shopping
                list.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Bedrooms */}
                <div className="flex flex-col gap-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <BedDouble className="w-4 h-4 text-muted-foreground" />
                    Bedrooms
                  </Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDecrement(setBedrooms, bedrooms)}
                      disabled={bedrooms === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-bold w-8 text-center">
                      {bedrooms}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleIncrement(setBedrooms, bedrooms)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Bathrooms */}
                <div className="flex flex-col gap-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Bath className="w-4 h-4 text-muted-foreground" />
                    Bathrooms
                  </Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDecrement(setBathrooms, bathrooms)}
                      disabled={bathrooms === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-bold w-8 text-center">
                      {bathrooms}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleIncrement(setBathrooms, bathrooms)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Living Rooms */}
                <div className="flex flex-col gap-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Sofa className="w-4 h-4 text-muted-foreground" />
                    Living Rooms
                  </Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        handleDecrement(setLivingRooms, livingRooms)
                      }
                      disabled={livingRooms === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-bold w-8 text-center">
                      {livingRooms}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        handleIncrement(setLivingRooms, livingRooms)
                      }
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generated List — gated behind contact form */}
          <LeadGate
            unlocked={unlocked}
            onUnlock={unlock}
            title="Unlock Your Furniture List"
            subtitle="Enter your contact info to reveal the full room-by-room shoppable Amazon checklist. We'll also send you a free rental analysis."
            ctaLabel="Unlock My Furniture List"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <h2 className="text-3xl font-heading font-bold flex items-center gap-3">
                  <ShoppingCart className="w-8 h-8 text-primary" />
                  Your Custom Shopping List
                </h2>
                {viewedItems.size > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      {viewedItems.size} item{viewedItems.size !== 1 ? "s" : ""}{" "}
                      viewed
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearViewed}
                      className="text-xs"
                    >
                      Clear viewed
                    </Button>
                  </div>
                )}
              </div>

              {/* Supplies Items */}
              {renderItemList(
                "Supplies & Guest Amenities",
                furnitureData.supplies,
                1,
                <Package className="w-6 h-6" />,
                "supplies",
              )}

              {/* General Items */}
              {renderItemList(
                "General Essentials",
                furnitureData.general,
                1,
                <Home className="w-6 h-6" />,
                "general",
              )}

              {/* Kitchen */}
              {renderItemList(
                "Kitchen",
                furnitureData.kitchen,
                1,
                <Utensils className="w-6 h-6" />,
                "kitchen",
              )}

              {/* Dining Room */}
              {renderItemList(
                "Dining Room",
                furnitureData.diningRoom,
                1,
                <UtensilsCrossed className="w-6 h-6" />,
                "dining",
              )}

              {/* Living Rooms */}
              {renderLivingRooms()}

              {/* Bedrooms */}
              {Array.from({ length: bedrooms }).map((_, roomIndex) => {
                const bedroomKey =
                  `bedroom${roomIndex + 1}` as keyof typeof furnitureData;
                const items =
                  (
                    furnitureData as Record<
                      string,
                      {
                        name: string;
                        desc: string;
                        link: string;
                        qty: number;
                      }[]
                    >
                  )[bedroomKey] || furnitureData.bedroom1;
                return (
                  <Card
                    key={`bed-${roomIndex}`}
                    className="mb-8 border-primary/10 shadow-sm"
                  >
                    <CardHeader className="bg-muted/30 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                          <BedDouble className="w-6 h-6" />
                        </div>
                        <CardTitle className="text-2xl font-heading">
                          Bedroom {roomIndex + 1}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((item, idx) => (
                          <div key={idx}>{renderItemCard(item)}</div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Main Floor Bedroom */}
              {renderItemList(
                "Main Floor Bedroom",
                furnitureData.mainFloorBedroom,
                1,
                <BedDouble className="w-6 h-6" />,
                "main-floor-bed",
              )}

              {/* Bathrooms */}
              {renderItemList(
                "Bathroom",
                furnitureData.bathroom,
                bathrooms,
                <Bath className="w-6 h-6" />,
                "bath",
              )}

              {/* Basement / Game Room */}
              {renderItemList(
                "Basement & Game Room",
                furnitureData.basement,
                1,
                <Gamepad2 className="w-6 h-6" />,
                "basement",
              )}
            </div>
          </LeadGate>
        </div>
      </main>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={
          <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
        }
      >
        <Suspense
          fallback={
            <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
          }
        >
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default FurniturePage;
